import { FileText, Search, BarChart3, CheckCircle, XCircle, Ban } from 'lucide-react';
import { ISavV2, SavStatus } from '@/models/savV2';
import { DecisionStatus } from '@/models/savV2Decision';
import { StepperNav, type StepperStep } from '@/Components/shared/StepperNav';
import { DeclarationStep } from './steps/DeclarationStep';
import { RepairerSearchStep } from './steps/RepairerSearchStep';
import { AnalysisStep } from './steps/AnalysisStep';
import { DecisionStep } from './steps/DecisionStep';
import { formatDateTime } from '@/utils/dateUtils';

interface SavDecisionTabProps {
  sav: ISavV2;
  isV1Sav: boolean;
  quote: any;
  decision: any;
  showValidateAndSignButton: boolean;
  showSignatureButton: boolean;
  showContactButton: boolean;
  isValidating: boolean;
  onAccessDecisionClick: () => void;
  onContactClick: () => void;
}

// Mapping virtualStatus.phase → step index (4 étapes)
const PHASE_TO_STEP: Record<string, number> = {
  declaration: 0,
  quote_acquisition: 2,
  analysis: 2,
  validation: 3,
  repair: 3,
  completed: 3,
};

const STEP_DEFS = [
  { id: 'declaration', label: 'Déclaration', icon: FileText },
  { id: 'repairer', label: 'Recherche réparateur', icon: Search },
  { id: 'analysis', label: 'Analyse Affinicar', icon: BarChart3 },
  { id: 'decision', label: 'Décision', icon: CheckCircle },
];

export function SavDecisionTab({
  sav,
  isV1Sav,
  quote,
  decision,
  showValidateAndSignButton,
  showSignatureButton,
  showContactButton,
  isValidating,
  onAccessDecisionClick,
  onContactClick,
}: SavDecisionTabProps) {
  // Trouver le premier virtualStatus avec une phase connue (ignorer les alertes phase=unknown)
  const virtualStatus = sav.virtualStatuses?.find(vs => vs.phase && vs.phase !== 'unknown') || sav.virtualStatuses?.[0];
  const phase = virtualStatus?.phase;

  // Dossier classé sans suite : sa phase est 'completed' (statut final) mais il
  // ne faut PAS afficher un parcours abouti — on montre la progression RÉELLE
  // atteinte au moment de l'abandon + un bandeau explicite.
  // Détection par stateId : dans l'outil pro, les virtualStatuses arrivent par
  // un endpoint séparé (useSavDetail) et ne sont PAS attachés à l'objet sav —
  // sav.virtualStatuses y est toujours undefined (le find() ci-dessus ne sert
  // que si un jour le payload les inclut). 9 = SANS_SUITE (ID_ETAT_SINISTRE).
  const isSansSuite =
    sav.stateId === SavStatus.SANS_SUITE ||
    !!sav.virtualStatuses?.some(vs => vs.status === 'SANS_SUITE');

  // Déduire la step depuis la phase, ou depuis l'état réel du SAV si aucune
  // phase connue — et TOUJOURS depuis l'état réel pour un sans suite (la phase
  // 'completed' forcerait l'étape Décision même si le dossier s'est arrêté à
  // la recherche réparateur).
  let currentStepIndex: number;
  if (!isSansSuite && phase && PHASE_TO_STEP[phase] !== undefined) {
    currentStepIndex = PHASE_TO_STEP[phase];
  } else if (decision) {
    currentStepIndex = 3;
  } else if (quote) {
    currentStepIndex = 2;
  } else if (sav.currentIntervention) {
    currentStepIndex = sav.currentIntervention.repairerAssignedAt ? 2 : 1;
  } else {
    currentStepIndex = 0;
  }

  // Cycle précédent : chercher un devis non sélectionné dans l'intervention courante
  const allQuotes = sav.currentIntervention?.quotes ?? [];
  const previousQuote = allQuotes.length > 1
    ? allQuotes.find(q => q.id !== sav.currentIntervention?.selectedQuoteId) ?? null
    : null;
  const allDecisions = sav.currentIntervention?.decisions ?? [];
  // La step Décision est complétée selon le type de décision
  const isDecisionCompleted = (() => {
    if (!decision) return false;
    const dt = decision.decisionType;
    // Accord total ou partiel → complété quand le bénéficiaire a signé
    if (dt === 'ACCORD' || dt === 'PARTIAL') {
      return !!decision.signatures?.beneficiary?.signedAt;
    }
    // Refus → complété quand le garant a signé
    if (dt === 'REFUSAL') {
      return !!decision.signatures?.garant?.signedAt;
    }
    return false;
  })();

  const stepCompletedDates: (string | null)[] = [
    sav.declarationDate ?? sav.createdAt ?? null,
    sav.currentIntervention?.repairerAssignedAt ?? sav.currentIntervention?.createdAt ?? null,
    quote?.analysisData?.analyzedAt ?? null,
    decision?.signatures?.beneficiary?.signedAt ?? decision?.signatures?.garant?.signedAt ?? decision?.submittedAt ?? null,
  ];

  const stepperSteps: StepperStep[] = STEP_DEFS.map((def, i) => {
    const Icon = def.icon;
    const status = i < currentStepIndex
      ? 'completed' as const
      : i === currentStepIndex
        ? (i === 3 && isDecisionCompleted
            ? 'completed' as const
            // Sans suite : l'étape atteinte n'est ni « en cours » ni terminée —
            // le dossier s'est arrêté là (rendu neutre, pas de fausse activité)
            : isSansSuite
              ? 'pending' as const
              : 'active' as const)
        : 'locked' as const;
    return {
      id: def.id,
      label: def.label,
      icon: <Icon className="tw-w-3.5 tw-h-3.5" />,
      status,
      completedAt: status === 'completed' ? stepCompletedDates[i] : null,
    };
  });


  const handleStepClick = (index: number) => {
    if (index > currentStepIndex) return;
    const ids = ['declaration', 'repairer', 'analysis', 'decision'];
    const el = document.getElementById(`step-${ids[index]}`);
    if (el) {
      const offset = 140;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="tw-space-y-4">
      {/* Stepper 4 étapes */}
      {!isV1Sav && (
        <div className="tw-sticky tw-top-16 tw-z-30 tw-bg-gray-50 tw-py-2">
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-3">
            <StepperNav
              steps={stepperSteps}
              // -1 aussi pour un sans suite : StepperNav surligne « en cours »
              // par activeStep (pas par step.status) — sans ça, l'étape
              // atteinte resterait affichée comme active sur un dossier
              // abandonné.
              activeStep={
                isSansSuite || (isDecisionCompleted && currentStepIndex === 3)
                  ? -1
                  : currentStepIndex
              }
              onStepClick={handleStepClick}
            />
          </div>
        </div>
      )}

      {/* Bandeau sans suite — le badge d'en-tête dit « Sans suite » mais le
          parcours d'étapes seul pourrait laisser croire à un dossier abouti */}
      {isSansSuite && (
        <div className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-100">
          <Ban className="tw-w-5 tw-h-5 tw-text-gray-600 tw-flex-shrink-0 tw-mt-0.5" />
          <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-mb-0">
            Ce dossier a été classé sans suite — il n'est plus en cours de traitement.
          </p>
        </div>
      )}

      {/* Bandeau refus garant */}
      {decision?.status === DecisionStatus.GARANT_REFUSED && (
        <div className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50">
          <XCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
          <div>
            <p className="tw-text-sm tw-font-medium tw-text-red-800 tw-mb-0">
              Recommandation refusée par le garant
              {decision.garantValidation?.decidedAt && (
                <span className="tw-font-normal tw-text-red-600"> — le {formatDateTime(decision.garantValidation.decidedAt)}</span>
              )}
            </p>
            {decision.garantValidation?.reason && (
              <p className="tw-text-sm tw-text-red-700 tw-mt-1 tw-mb-0">
                Motif : {decision.garantValidation.reason}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Steps empilées — toutes les steps accessibles sont affichées */}
      <div id="step-declaration">
        <DeclarationStep sav={sav} completedAt={currentStepIndex > 0 ? stepCompletedDates[0] : null} />
      </div>

      {currentStepIndex >= 1 && (
        <div id="step-repairer">
          <RepairerSearchStep
            sav={sav}
            virtualStatus={virtualStatus?.status}
            completedAt={currentStepIndex > 1 ? stepCompletedDates[1] : null}
          />
        </div>
      )}

      {currentStepIndex >= 2 && (
        <div id="step-analysis">
          <AnalysisStep
            sav={sav}
            quote={quote}
            decision={decision}
            completedAt={currentStepIndex > 2 ? stepCompletedDates[2] : null}
            previousQuote={previousQuote}
          />
        </div>
      )}

      {currentStepIndex >= 3 && decision && decision.status !== 'DRAFT' && (
        <div id="step-decision">
          <DecisionStep
            sav={sav}
            decision={decision}
            showValidateAndSignButton={showValidateAndSignButton}
            showSignatureButton={showSignatureButton}
            showContactButton={showContactButton}
            isValidating={isValidating}
            onAccessDecisionClick={onAccessDecisionClick}
            onContactClick={onContactClick}
            completedAt={isDecisionCompleted ? stepCompletedDates[3] : null}
            allDecisions={allDecisions}
          />
        </div>
      )}
    </div>
  );
}
